import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Star, Trophy, Sparkles, RefreshCw, ChevronDown, Loader2, CheckCircle2, Mail } from "lucide-react";
import { tiers, packages } from "@/data/pricing";
import { submitInquiry } from "@/lib/api";

const formatR = (n) =>
    new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(n);

/* ---------- Question definitions ---------- */
const QUESTIONS = [
    {
        id: "vat",
        title: "Is your company VAT-registered?",
        help: "VAT-registration triggers monthly/bi-monthly VAT returns and Reviewed Financial Statements.",
        options: [
            { value: "yes", label: "Yes, VAT-registered" },
            { value: "no", label: "No, not VAT-registered" },
        ],
    },
    {
        id: "bankAudit",
        title: "Does your bank overdraft / loan agreement require an audit?",
        help: "Many lenders require Audited Annual Financial Statements as a covenant.",
        options: [
            { value: "yes", label: "Yes, audit is required" },
            { value: "no", label: "No, not required" },
            { value: "unsure", label: "I'm unsure" },
        ],
    },
    {
        id: "moiAudit",
        title: "Does your Memorandum of Incorporation (MOI) require an audit?",
        help: "Some MOIs (especially with external shareholders or trustees) mandate audits regardless of size.",
        options: [
            { value: "yes", label: "Yes, MOI requires audit" },
            { value: "no", label: "No, not required" },
            { value: "unsure", label: "I'm unsure" },
        ],
    },
    {
        id: "payroll",
        title: "How many people are on your payroll?",
        help: "Includes directors, full-time and regularly-paid part-time staff.",
        options: [
            { value: "0-2", label: "0 – 2 staff" },
            { value: "3-5", label: "3 – 5 staff" },
            { value: "6-15", label: "6 – 15 staff" },
            { value: "16-40", label: "16 – 40 staff" },
            { value: "40+", label: "More than 40 staff" },
        ],
    },
    {
        id: "meetings",
        title: "Do you want regular advisory meetings with your accountant?",
        help: "Meetings cover tax planning, profitability reviews and CFO-style guidance.",
        options: [
            { value: "none", label: "No — only when I request one" },
            { value: "bimonthly", label: "Yes — every 2 months" },
            { value: "monthly", label: "Yes — monthly" },
        ],
    },
    {
        id: "workSplit",
        title: "How much of the day-to-day accounting will you do yourself?",
        help: "TCA can do as little or as much of the bookkeeping as you'd like.",
        options: [
            { value: "self", label: "I'll do most of it (TCA does ~20%)" },
            { value: "shared", label: "We'll share it (TCA does ~50%)" },
            { value: "handsoff", label: "Hands-off — TCA does ~80%" },
        ],
    },
    {
        id: "specialist",
        title: "Who would you prefer as your designated specialist?",
        help: "Bookkeepers handle capture & compliance; SAICA Accountants add advisory depth.",
        options: [
            { value: "bookkeeper", label: "A Qualified Bookkeeper" },
            { value: "saica", label: "A SAICA / SAIPA Accountant" },
            { value: "either", label: "Either is fine" },
        ],
    },
];

/* ---------- Recommendation logic ---------- */
const PAYROLL_RANK = { "0-2": 1, "3-5": 2, "6-15": 3, "16-40": 4, "40+": 5 };

// Each tier's package payroll capacity in ranks (Bronze, Silver, Gold)
// Starter: Bronze=2 (rank1), Silver=5 (rank2), Gold=10 (rank3)
// Core:    Bronze=5 (rank2), Silver=20 (rank4), Gold=40 (rank4 borderline) — bump 40+ to gold
// Elite:   Bronze=15 (rank3), Silver=40 (rank4), Gold=80 (rank5)
const PAYROLL_CAPS = {
    starter: { Bronze: 1, Silver: 2, Gold: 3 },
    core:    { Bronze: 2, Silver: 4, Gold: 5 },
    elite:   { Bronze: 3, Silver: 4, Gold: 5 },
};

function computeRecommendation(answers) {
    // 1. Tier selection
    // "unsure" on audit means NOT required (per spec)
    const auditRequired = answers.bankAudit === "yes" || answers.moiAudit === "yes";
    let tierId;
    if (auditRequired) tierId = "elite";
    else if (answers.vat === "yes") tierId = "core";
    else tierId = "starter";

    // 2. Initial package from work-split
    let pkgIdx; // 0=Bronze, 1=Silver, 2=Gold
    if (answers.workSplit === "self") pkgIdx = 0;
    else if (answers.workSplit === "shared") pkgIdx = 1;
    else pkgIdx = 2;

    // 3. Bump for payroll capacity
    const payrollRank = PAYROLL_RANK[answers.payroll];
    const caps = PAYROLL_CAPS[tierId];
    const pkgNames = ["Bronze", "Silver", "Gold"];
    while (pkgIdx < 2 && payrollRank > caps[pkgNames[pkgIdx]]) {
        pkgIdx += 1;
    }

    // 4. Bump for monthly meetings (only Gold offers monthly across all tiers)
    if (answers.meetings === "monthly" && pkgIdx < 2) pkgIdx = 2;
    // Bump for bi-monthly meetings (Bronze is "upon request" only)
    if (answers.meetings === "bimonthly" && pkgIdx < 1) pkgIdx = 1;

    // 5. Bump for SAICA Accountant requirement (Bronze typically uses Bookkeeper)
    if (answers.specialist === "saica" && pkgIdx < 1) pkgIdx = 1;

    // 6. Elite Bronze is Non-VAT only — bump VAT companies to Silver
    if (tierId === "elite" && answers.vat === "yes" && pkgIdx < 1) pkgIdx = 1;

    return { tierId, pkgIdx };
}

/* ---------- Card feature rows — shared between card and email payload ---------- */
function buildCardFeatures(pkg) {
    return [
        { label: "VAT-registered", value: pkg.features.vatRegistered ? "Yes" : "No" },
        { label: "Financial Statements", value: pkg.features.statementType },
        { label: "Company Tax Returns", value: "Included" },
        { label: "Specialist", value: pkg.features.specialist },
        { label: "Workload", value: pkg.features.workSplit },
        { label: "Payroll", value: pkg.features.payroll },
        { label: "Advisory Meetings", value: pkg.features.discussionMeetings },
        { label: "Management Accounts", value: pkg.features.managementAccounts },
    ];
}

/* ---------- Lead capture form ---------- */
const ANSWER_LABELS = {
    vat: { yes: "Yes — VAT-registered", no: "No — not VAT-registered" },
    bankAudit: { yes: "Yes — required", no: "Not required", unsure: "Unsure (treated as not required)" },
    moiAudit: { yes: "Yes — required", no: "Not required", unsure: "Unsure (treated as not required)" },
    payroll: { "0-2": "0–2 staff", "3-5": "3–5 staff", "6-15": "6–15 staff", "16-40": "16–40 staff", "40+": "More than 40 staff" },
    meetings: { none: "Only on request", bimonthly: "Every 2 months", monthly: "Monthly" },
    workSplit: { self: "Client does most (TCA ~20%)", shared: "Shared (TCA ~50%)", handsoff: "Hands-off (TCA ~80%)" },
    specialist: { bookkeeper: "Qualified Bookkeeper", saica: "SAICA / SAIPA Accountant", either: "Either is fine" },
};

function buildAnswerPairs(answers) {
    return QUESTIONS.map((q) => ({
        question: q.title,
        answer: ANSWER_LABELS[q.id]?.[answers[q.id]] || answers[q.id] || "—",
    }));
}

function buildInquiryMessage(answers, tier, pkg, refineCount) {
    // Plain-text fallback (also stored on the inquiry record for the admin list).
    const pairs = buildAnswerPairs(answers);
    const lines = [
        `PACKAGE MATCHMAKER LEAD`,
        ``,
        `Recommended package : ${tier.name} ${pkg.name}`,
        `Monthly             : R${pkg.monthly.toLocaleString("en-ZA")} + Xero`,
        `Annual              : R${pkg.annual.toLocaleString("en-ZA")} + Xero`,
    ];
    if (refineCount > 0) {
        lines.push(`Budget refines used : ${refineCount}`);
    }
    lines.push(``, `Answers given:`);
    pairs.forEach((p, i) => {
        lines.push(`  ${String(i + 1).padStart(2, " ")}. ${p.question}`);
        lines.push(`      → ${p.answer}`);
    });
    return lines.join("\n");
}

function LeadCaptureForm({ answers, tier, pkg, refineCount }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
    const [status, setStatus] = useState("idle"); // idle | loading | success
    const [errors, setErrors] = useState({});

    const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    const emailTrim = form.email.trim();
    const nameTrim = form.name.trim();
    const emailValid = EMAIL_RE.test(emailTrim);
    const nameValid = nameTrim.length >= 2;
    const showEmailError = emailTrim.length > 0 && !emailValid;
    const canSubmit = nameValid && emailValid && status !== "loading";

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const validate = () => {
        const er = {};
        if (!nameValid) er.name = "Please enter your full name.";
        if (!emailValid) er.email = "Please enter a valid email address.";
        return er;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const er = validate();
        if (Object.keys(er).length) {
            setErrors(er);
            return;
        }
        setStatus("loading");
        try {
            await submitInquiry({
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                company: form.company.trim() || null,
                subject: `Package Matchmaker — ${tier.name} ${pkg.name}`,
                message: buildInquiryMessage(answers, tier, pkg, refineCount),
                source: "matchmaker",
                matchmaker: {
                    tier_id: tier.id,
                    tier_name: tier.name,
                    package_name: pkg.name,
                    blurb: pkg.blurb,
                    monthly: pkg.monthly,
                    annual: pkg.annual,
                    features: buildCardFeatures(pkg),
                    answers: buildAnswerPairs(answers),
                    refine_count: refineCount,
                },
            });
            setStatus("success");
            toast.success("Recommendation sent — check your inbox in a few seconds.");
        } catch (err) {
            console.error(err);
            setStatus("idle");
            toast.error("Something went wrong. Please try again or contact us directly.");
        }
    };

    if (status === "success") {
        return (
            <div
                data-testid="matchmaker-lead-success"
                className="bg-white border border-tca-orange p-7 md:p-9 text-center"
            >
                <div className="w-12 h-12 mx-auto bg-tca-orange/10 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-tca-orange" />
                </div>
                <h3 className="font-display font-extrabold text-tca-ink text-2xl mb-2">
                    Thanks {form.name.split(" ")[0]} — your recommendation is on its way.
                </h3>
                <p className="text-tca-mute leading-relaxed max-w-lg mx-auto">
                    We've logged your <strong className="text-tca-ink">{tier.name} {pkg.name}</strong> match and a TC Auditors
                    advisor will reach out to {form.email} within one business day.
                </p>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            data-testid="matchmaker-lead-form"
            className="bg-white border border-tca-line p-7 md:p-9"
            noValidate
        >
            <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-tca-orange/10 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-tca-orange" />
                </div>
                <div>
                    <h3 className="font-display font-extrabold text-tca-ink text-2xl leading-tight">
                        Send me this recommendation
                    </h3>
                    <p className="text-sm text-tca-mute mt-1">
                        We'll email you the {tier.name} {pkg.name} fit summary and a TC Auditors advisor will follow up. No spam, no commitment.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Full name <span className="text-tca-orange">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        data-testid="matchmaker-lead-name"
                        className={`w-full px-4 py-3 border bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors ${
                            errors.name ? "border-red-500" : "border-tca-line"
                        }`}
                        placeholder="Jane Smith"
                    />
                    {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Email <span className="text-tca-orange">*</span>
                    </label>
                    <input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        data-testid="matchmaker-lead-email"
                        aria-invalid={showEmailError || Boolean(errors.email)}
                        className={`w-full px-4 py-3 border bg-white text-tca-ink focus:outline-none transition-colors ${
                            showEmailError || errors.email
                                ? "border-red-500 bg-red-50 focus:border-red-500"
                                : "border-tca-line focus:border-tca-orange"
                        }`}
                        placeholder="jane@company.co.za"
                    />
                    {(showEmailError || errors.email) && (
                        <p className="text-xs text-red-600 mt-1" data-testid="matchmaker-lead-email-error">
                            {errors.email || "Please enter a valid email address."}
                        </p>
                    )}
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Phone <span className="text-tca-mute/60 normal-case tracking-normal font-normal">(optional)</span>
                    </label>
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        data-testid="matchmaker-lead-phone"
                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                        placeholder="079 461 7545"
                    />
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Company <span className="text-tca-mute/60 normal-case tracking-normal font-normal">(optional)</span>
                    </label>
                    <input
                        type="text"
                        value={form.company}
                        onChange={update("company")}
                        data-testid="matchmaker-lead-company"
                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                        placeholder="Acme (Pty) Ltd"
                    />
                </div>
            </div>

            <button
                type="submit"
                disabled={!canSubmit}
                aria-disabled={!canSubmit}
                title={!canSubmit && emailTrim.length > 0 && !emailValid ? "Please enter a valid email address" : undefined}
                data-testid="matchmaker-lead-submit"
                className="btn-primary group mt-6 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {status === "loading" ? (
                    <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                ) : (
                    <>
                        Send Me My {tier.name} {pkg.name} Package
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                )}
            </button>

            <p className="text-[11px] text-tca-mute mt-4">
                By submitting, you consent to TC Auditors contacting you about your inquiry. We'll never share your details.
            </p>
        </form>
    );
}


/* ---------- Recommendation card (mirrors Pricing card features) ---------- */
function RecommendationCard({ tierId, pkgIdx }) {
    const tier = tiers.find((t) => t.id === tierId);
    const pkg = packages[tierId][pkgIdx];

    const featureRows = buildCardFeatures(pkg).map((f) => [
        f.label,
        f.value === "Included" ? <Check key={f.label} className="w-4 h-4 text-tca-orange" /> : f.value,
    ]);

    return (
        <div className="relative" data-testid="recommendation-card-wrapper">
            {/* Animated glow */}
            <div
                aria-hidden="true"
                className="absolute -inset-1 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(120deg, rgba(242,107,31,0.55), rgba(242,107,31,0.05) 35%, rgba(242,107,31,0.55) 70%, rgba(242,107,31,0.05))",
                    backgroundSize: "300% 300%",
                    animation: "tca-prize-glow 4s ease-in-out infinite",
                    filter: "blur(14px)",
                }}
            />

            <article
                data-testid={`recommendation-card-${tierId}-${pkg.name.toLowerCase()}`}
                className="relative bg-white border-2 border-tca-orange p-8 lg:p-10"
                style={{ boxShadow: "0 0 0 1px #F26B1F, 0 20px 60px -15px rgba(242,107,31,0.5)" }}
            >
                {/* Prize ribbon */}
                <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-tca-orange text-white px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold">
                    <Trophy className="w-3 h-3 fill-current" /> Your match
                </span>

                <p className="overline mb-3">{tier.name}</p>
                <h3 className="font-display font-extrabold text-tca-ink text-3xl mb-2">{pkg.name}</h3>
                <p className="text-sm text-tca-mute mb-7 min-h-[40px]">{pkg.blurb}</p>

                <div className="mb-7">
                    <p className="font-display font-extrabold text-tca-ink text-5xl tracking-tight tabular-nums">
                        {formatR(pkg.monthly)}
                    </p>
                    <p className="text-xs text-tca-mute mt-1">per month + Xero</p>
                    <p className="text-xs text-tca-mute mt-0.5">
                        or {formatR(pkg.annual)} <span className="opacity-70">per year</span>
                    </p>
                </div>

                <ul className="space-y-2.5 mb-8">
                    {featureRows.map(([k, v]) => (
                        <li key={k} className="flex items-center justify-between text-sm border-b border-tca-line pb-2">
                            <span className="text-tca-mute shrink-0">{k}</span>
                            <span className="text-tca-ink font-semibold text-right ml-2">{v}</span>
                        </li>
                    ))}
                </ul>

                <a
                    href={`https://retainer.tcauditors.co.za/${tierId}-${pkg.name.toLowerCase()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`matchmaker-cta-${tierId}-${pkg.name.toLowerCase()}`}
                    className="w-full btn-primary group"
                >
                    Get {tier.name} {pkg.name}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </a>
            </article>
        </div>
    );
}

/* ---------- Main Page ---------- */
export default function PackageMatchmaker() {
    const [step, setStep] = useState(0); // 0..QUESTIONS.length-1, then QUESTIONS.length = result
    const [answers, setAnswers] = useState({});
    const [refineCount, setRefineCount] = useState(0); // how many tiers we've stepped down

    const baseRecommendation = useMemo(
        () => (Object.keys(answers).length === QUESTIONS.length ? computeRecommendation(answers) : null),
        [answers]
    );

    const recommendation = useMemo(() => {
        if (!baseRecommendation) return null;
        const idx = Math.max(0, baseRecommendation.pkgIdx - refineCount);
        return { tierId: baseRecommendation.tierId, pkgIdx: idx };
    }, [baseRecommendation, refineCount]);

    const isResultStep = step >= QUESTIONS.length;
    const currentQ = QUESTIONS[step];
    const progress = Math.round(((isResultStep ? QUESTIONS.length : step) / QUESTIONS.length) * 100);

    function selectAnswer(value) {
        setAnswers((prev) => ({ ...prev, [currentQ.id]: value }));
        // Auto-advance
        setTimeout(() => setStep((s) => s + 1), 180);
    }

    function restart() {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setAnswers({});
        setStep(0);
        setRefineCount(0);
    }

    const canRefine = recommendation && recommendation.pkgIdx > 0;
    const pkgNames = ["Bronze", "Silver", "Gold"];
    const nextLowerName = recommendation ? pkgNames[Math.max(0, recommendation.pkgIdx - 1)] : null;
    const recommendedTierName = recommendation
        ? tiers.find((t) => t.id === recommendation.tierId)?.name
        : null;

    return (
        <div data-testid="page-matchmaker" className="bg-tca-fog min-h-[80vh]">
            {/* Top bar with return button */}
            <div className="bg-white border-b border-tca-line">
                <div className="container-tca py-5 flex items-center justify-between gap-4">
                    <Link
                        to="/pricing"
                        data-testid="matchmaker-return-btn"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-tca-ink hover:text-tca-orange transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Return to Pricing
                    </Link>
                    <p className="overline text-tca-orange hidden sm:block">Package Matchmaker</p>
                </div>
            </div>

            {/* Hero */}
            <section className="bg-white border-b border-tca-line">
                <div className="container-tca pt-12 pb-10 md:pt-16 md:pb-12">
                    <div className="max-w-3xl">
                        <p className="overline text-tca-orange mb-3 inline-flex items-center gap-2">
                            <Sparkles className="w-3.5 h-3.5" /> Find your fit in 60 seconds
                        </p>
                        <h1 className="font-display font-extrabold text-tca-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.0]">
                            Answer a few questions.
                            <span className="block text-tca-orange">We'll match the right package.</span>
                        </h1>
                        <p className="mt-5 text-tca-mute text-lg leading-relaxed">
                            Skip the side-by-side spreadsheet. Tell us how your business runs and we'll surface the
                            single package built for you — Starter, Core or Elite, in Bronze, Silver or Gold.
                        </p>
                    </div>
                </div>
            </section>

            {/* Quiz / Result */}
            <section className="container-tca py-12 md:py-16">
                {!isResultStep && (
                    <div className="max-w-3xl mx-auto" data-testid="matchmaker-quiz">
                        {/* Progress */}
                        <div className="mb-8">
                            <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] font-bold text-tca-mute mb-2">
                                <span>
                                    Question {step + 1} of {QUESTIONS.length}
                                </span>
                                <span data-testid="matchmaker-progress-pct">{progress}%</span>
                            </div>
                            <div className="h-1 bg-tca-line overflow-hidden">
                                <div
                                    className="h-full bg-tca-orange transition-all duration-500"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>

                        {/* Question */}
                        <div
                            key={currentQ.id}
                            className="bg-white border border-tca-line p-7 md:p-10 animate-fade-up"
                            data-testid={`matchmaker-q-${currentQ.id}`}
                        >
                            <h2 className="font-display font-extrabold text-tca-ink text-2xl sm:text-3xl leading-tight mb-2">
                                {currentQ.title}
                            </h2>
                            <p className="text-sm text-tca-mute mb-7">{currentQ.help}</p>

                            <div className="grid grid-cols-1 gap-3">
                                {currentQ.options.map((opt) => {
                                    const selected = answers[currentQ.id] === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => selectAnswer(opt.value)}
                                            data-testid={`matchmaker-option-${currentQ.id}-${opt.value}`}
                                            className={`group flex items-center justify-between gap-4 px-5 py-4 border text-left transition-all ${
                                                selected
                                                    ? "border-tca-orange bg-tca-orange/5"
                                                    : "border-tca-line bg-white hover:border-tca-ink hover:bg-tca-fog"
                                            }`}
                                        >
                                            <span className="text-base font-semibold text-tca-ink">
                                                {opt.label}
                                            </span>
                                            <span
                                                className={`w-5 h-5 border flex items-center justify-center shrink-0 transition-colors ${
                                                    selected
                                                        ? "border-tca-orange bg-tca-orange"
                                                        : "border-tca-line group-hover:border-tca-ink"
                                                }`}
                                            >
                                                {selected && <Check className="w-3 h-3 text-white" />}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Back step */}
                        {step > 0 && (
                            <div className="mt-6 flex justify-between items-center">
                                <button
                                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                                    data-testid="matchmaker-prev-btn"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-tca-mute hover:text-tca-ink transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" /> Previous question
                                </button>
                                <button
                                    onClick={restart}
                                    data-testid="matchmaker-restart-btn"
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-tca-mute hover:text-tca-orange uppercase tracking-[0.16em]"
                                >
                                    <RefreshCw className="w-3.5 h-3.5" /> Start over
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {isResultStep && recommendation && (
                    <div className="max-w-2xl mx-auto" data-testid="matchmaker-result">
                        <div className="text-center mb-10">
                            <p className="overline text-tca-orange mb-3 inline-flex items-center gap-2">
                                <Star className="w-3.5 h-3.5 fill-current" /> Your recommended package
                            </p>
                            <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl leading-tight">
                                Based on your answers, this is your fit.
                            </h2>
                            <p className="mt-3 text-tca-mute">
                                Pricing, features and inclusions stay perfectly in sync with the Pricing page.
                            </p>
                        </div>

                        <RecommendationCard tierId={recommendation.tierId} pkgIdx={recommendation.pkgIdx} />

                        <div className="mt-10" data-testid="matchmaker-lead-section">
                            <LeadCaptureForm
                                answers={answers}
                                tier={tiers.find((t) => t.id === recommendation.tierId)}
                                pkg={packages[recommendation.tierId][recommendation.pkgIdx]}
                                refineCount={refineCount}
                            />
                        </div>

                        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-center">
                            {canRefine && (
                                <button
                                    onClick={() => {
                                        setRefineCount((c) => c + 1);
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                    data-testid="matchmaker-refine-btn"
                                    className="group inline-flex items-center justify-center gap-2 bg-tca-ink text-white border border-tca-ink px-6 py-3.5 text-sm font-semibold tracking-wide hover:bg-tca-orange hover:border-tca-orange transition-colors duration-200 rounded-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tca-orange focus-visible:ring-offset-2"
                                >
                                    <ChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
                                    Tight Budget? Show Me The {recommendedTierName} {nextLowerName}
                                </button>
                            )}
                            <button
                                onClick={restart}
                                data-testid="matchmaker-restart-bottom-btn"
                                className="btn-secondary group"
                            >
                                <RefreshCw className="w-4 h-4 transition-transform group-hover:rotate-180" /> Start Over
                            </button>
                        </div>

                        {!canRefine && refineCount > 0 && (
                            <p className="mt-6 text-center text-xs text-tca-mute italic">
                                You've reached the lightest package in this tier. For an even leaner option, contact us
                                directly and we'll tailor a quote.
                            </p>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
}
